import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return '';
  const [year, month] = monthKey.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(month, 10) - 1]} '${year.slice(2)}`;
};

export const MonthlyTrendChart = ({ monthlyTrend = [], testId = 'chart-monthly-trend' }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-3">
      <CardTitle className="font-heading text-base">Monthly Session Trend</CardTitle>
      <CardDescription className="text-xs">Sessions delivered + completion rate over the last 12 months</CardDescription>
    </CardHeader>
    <CardContent>
      {monthlyTrend.length > 0 ? (
        <>
          <div className="flex items-center gap-5 mb-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-2 rounded-sm bg-[#C8E600] opacity-80" />
              Sessions
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-5 border-t-2 border-dashed border-[#f97316]" />
              Completion Rate %
            </span>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <ComposedChart data={monthlyTrend} margin={{ top: 5, right: 30, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="sessionGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#C8E600" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#C8E600" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fontSize: 10 }} tickLine={false} interval={1} />
              <YAxis yAxisId="sessions" tick={{ fontSize: 10 }} tickLine={false} allowDecimals={false} width={28} />
              <YAxis yAxisId="rate" orientation="right" tick={{ fontSize: 10 }} tickLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={36} />
              <RechartsTooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const sessions = payload.find(p => p.dataKey === 'sessions');
                  const rate = payload.find(p => p.dataKey === 'completion_rate');
                  return (
                    <div className="bg-white border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                      <p className="font-semibold mb-1">{formatMonthLabel(label)}</p>
                      {sessions && (
                        <p className="text-xs flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-[#C8E600] inline-block" />
                          {sessions.value} session{sessions.value !== 1 ? 's' : ''}
                        </p>
                      )}
                      {rate && rate.value != null ? (
                        <p className="text-xs flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-[#f97316] inline-block" />
                          {rate.value}% completed
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">No completions yet</p>
                      )}
                    </div>
                  );
                }}
              />
              <Area
                yAxisId="sessions" type="monotone" dataKey="sessions" name="Sessions"
                stroke="#C8E600" strokeWidth={2} fill="url(#sessionGradient)"
                dot={{ r: 3, fill: '#C8E600', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#C8E600' }}
              />
              <Line
                yAxisId="rate" type="monotone" dataKey="completion_rate" name="Completion Rate"
                stroke="#f97316" strokeWidth={2} strokeDasharray="4 3"
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (payload.completion_rate == null) return <g key={`dot-${props.index}`} />;
                  return <circle key={`dot-${props.index}`} cx={cx} cy={cy} r={3} fill="#f97316" strokeWidth={0} />;
                }}
                activeDot={{ r: 5, fill: '#f97316' }}
                connectNulls={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </>
      ) : (
        <p className="text-center text-muted-foreground py-8 text-sm">No session data</p>
      )}
    </CardContent>
  </Card>
);

export default MonthlyTrendChart;
